import type { ProblemDetails } from "../api/http/problemDetails";

/**
 * The one error type of the web app (NFR-5). Every failure the user can see is one of these
 * kinds, and `ErrorNotice` is the one component that displays it.
 */
export type UiError =
  /** The server answered with RFC 9457 problem details. */
  | { readonly kind: "problem"; readonly problem: ProblemDetails }
  /** No answer: the server is down, or the network or a proxy failed. */
  | { readonly kind: "serverUnreachable" }
  /** The server answered, but not in the form the API contract promises. */
  | {
      readonly kind: "unexpectedResponse";
      readonly status: number;
      readonly reason: UnexpectedResponseReason;
    }
  /** An answer this tab could not read, while a newer build of the app is served (F89). */
  | { readonly kind: "appOutdated" }
  /** A link in the address bar that does not name anything the app can open. */
  | { readonly kind: "invalidAddress"; readonly address: string }
  /** A programming error or an exception nobody expected. Always logged. */
  | { readonly kind: "unexpected"; readonly cause: unknown };

export type UnexpectedResponseReason = "notProblemDetails" | "invalidJson" | "contractViolation";

/** Carries a `UiError` through `throw` and promise rejection. */
export class UiErrorException extends Error {
  readonly uiError: UiError;

  constructor(uiError: UiError) {
    super(`UI error: ${uiError.kind}`);
    this.name = "UiErrorException";
    this.uiError = uiError;
  }
}

/** Turn anything caught into a `UiError`. Unknown exceptions keep their cause. */
export function toUiError(caught: unknown): UiError {
  if (caught instanceof UiErrorException) {
    return caught.uiError;
  }
  return { kind: "unexpected", cause: caught };
}

/** Whether the server answered with problem details of this code. */
export function isProblemWithCode(error: UiError, code: string): boolean {
  return error.kind === "problem" && error.problem.code === code;
}

/**
 * Log what the user cannot act on, so nothing is swallowed: unexpected exceptions, broken
 * responses and server errors (5xx). Expected states, such as a rejected request, are only
 * shown in the UI.
 */
export function reportUiError(error: UiError): void {
  switch (error.kind) {
    case "unexpected":
      console.error("Unexpected error in the web app.", error.cause);
      return;
    case "unexpectedResponse":
      console.error(`Unexpected server response (HTTP ${error.status}): ${error.reason}.`);
      return;
    case "problem":
      if (error.problem.status >= 500) {
        console.error(
          `Server error ${error.problem.code} (request ${error.problem.requestId}): ${error.problem.detail}`,
        );
      }
      return;
    case "serverUnreachable":
    case "appOutdated":
    case "invalidAddress":
      return;
  }
}
